import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notification } from 'components/ds';
import PageContainer from '../../../components/PageContainer/PageContainer';
import { pageConfigs } from '../../../configs/pageConfigs';
import { useOtp } from '../../../hooks/useOtp';
import { useSearchUser } from '../../../hooks/useSearchUser';
import {
  TwoFactorValidationCodeComponent
} from '../../../components/TwoFactorValidationCodeComponent/TwoFactorValidationCodeComponent';
import { FactorValidationWrapper } from '../ForgotPasswordOtpRequest/ForgotPasswordOtpRequest.styles';
import { OtpChannelEnum } from '../../../constantes/OtpChannelEnum';
import { useOtpContext } from '../../../context/OtpContextProvider';
import { useGetUserData } from '../../../hooks/useGetUserData';
import { ApiLocalStorage } from 'utils/ApiLocalStorage';
import { useSchema } from '../../../hooks/useSchema';
import { SIGNUP_TYPES } from '../../../_types/schema.types';
import { useParticipantId } from '../../../hooks/useParticipantId';
import { getNavigationDestination } from '../../../utils/getNavigationDestination';
import { useUsername } from '../../../hooks/useUsername';
import { useAuth } from '../../../../../auth/AuthProvider';
import { USER_DATA_KEY } from '../../../constantes/LocalStorageEnum';
import { ValidatorField } from '../../../../../components/ValidatorField';

export const OtpValidation: React.FC = () => {
  const [code, setCode] = useState('');
  const [isResendDisabled, setIsResendDisabled] = useState<boolean>(true);
  const navigate = useNavigate();
  const {getUserData: fetchUserData, loading: userDataLoading} = useGetUserData();
  const {userData} = useSearchUser();
  const {validateOtpCode, loading: validateCodeLoading, sendOtpRequest, otpId} = useOtp();
  const {selectedChannel, currentFlow} = useOtpContext();
  const {signupType} = useSchema();
  const {participantId} = useParticipantId();
  const {username} = useUsername();
  const {loginWithOtp} = useAuth();
  
  const handleValidateOtpCode = async () => {
    if (!otpId) {
      notification.error({
        message: 'Erro',
        description: 'OTP ID não encontrado. Por favor, solicite um novo código.'
      });

      return;
    }
    
    if (!participantId) {
      notification.error({
        message: 'Erro',
        description: 'Dados do usuário não encontrados. Por favor, faça login novamente.'
      });

      return;
    }
    
    const otpResponse = await validateOtpCode({
      otpId,
      code,
      flow: currentFlow,
      generateToken: true,
      participantId,
      signUpType: signupType
    });
    
    if (otpResponse && otpResponse.validCode) {
      const isOpenFlow = signupType === SIGNUP_TYPES.OPEN;
      
      console.log('OTP validado com sucesso:', {
        isOpenFlow,
        currentFlow,
        hasAccessToken: !!otpResponse.accessToken,
        hasExpiresIn: !!otpResponse.expiresIn,
        username
      });
      
      let loginSuccess = true;
      
      // **SEMPRE BUSCAR DADOS DO USUÁRIO VIA /account/me/data** independente do fluxo
      try {
        const response = await fetchUserData();
        ApiLocalStorage.set(USER_DATA_KEY, response);
        console.log('Dados do usuário buscados via /account/me/data e salvos no localStorage');
        
        // **FAZER LOGIN AUTOMÁTICO APÓS REGISTRO** - Para fluxos abertos ou quando há accessToken
        if (otpResponse.accessToken && username) {
          console.log('Fazendo login automático após OTP validado');
          
          try {
            const cleanedUsername = ValidatorField.removeSpecialCharsFromUsername(username);
            const loginResult = await loginWithOtp(
              cleanedUsername,
              otpResponse.accessToken,
              otpResponse.expiresIn || '3600',
              response
            );
            
            console.log('Login automático bem-sucedido:', {
              hasProfile: !!loginResult.profile,
              username: loginResult.profile?.username
            });
            
            loginSuccess = true;
            
          } catch (error) {
            console.error('Erro no login automático:', error);
            notification.error({
              message: 'Erro',
              description: 'Não foi possível fazer login automático, mas o registro foi concluído.'
            });
            // Não bloquear o fluxo se o login falhar
            loginSuccess = true;
          }
        }
        
      } catch (error) {
        console.error('Erro ao buscar dados do usuário via /account/me/data:', error);
        notification.error({
          message: 'Erro',
          description: 'Não foi possível buscar os dados do usuário'
        });
        loginSuccess = false;
      }
      
      if (loginSuccess) {
        // **CASO ESPECIAL**: Verificar se é recuperação de onboarding
        const isOnboardingRecovery = !isOpenFlow &&
          currentFlow === 'Authentication' &&
          userData?.userFound &&
          !userData?.firstAccess &&
          !userData?.onboardingFinished;
        
        const destination = getNavigationDestination({
          isOpenFlow,
          currentFlow: currentFlow || '',
          isOnboardingRecovery
        });
        
        console.log('Navegação determinada:', {
          isOpenFlow,
          currentFlow,
          destination,
          loginSuccess,
          isOnboardingRecovery,
          userFirstAccess: userData?.firstAccess,
          userOnboardingFinished: userData?.onboardingFinished
        });
        
        // Aguardar um pouco para garantir que o estado foi atualizado
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        navigate(destination);
      } else {
        console.error('Login não foi bem-sucedido, não navegando');
      }
    }
  };
  
  const handleResend = async () => {
    if (!participantId || !currentFlow) {
      notification.error({
        message: 'Erro',
        description: 'Dados do usuário não encontrados. Por favor, faça login novamente.'
      });

      return;
    }
    
    try {
      const request = {
        participantId,
        flow: currentFlow,
        channel: selectedChannel
      };
      
      await sendOtpRequest(request);
      setIsResendDisabled(true);
    } catch (error) {
      console.error('Erro ao reenviar código:', error);
      notification.error({
        message: 'Erro ao reenviar código',
        description: 'Não foi possível reenviar o código. Por favor, tente novamente.'
      });
    }
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.slice(0, 6);
    setCode(value);
  };
  
  const channelValue = selectedChannel === OtpChannelEnum.EMAIL
    ? userData?.emailObfuscated
    : userData?.phoneObfuscated;
  
  const codeLength = 6;
  const isLoading = userDataLoading || validateCodeLoading;
  
  return (
    <PageContainer
      title={pageConfigs.validateOtpResetPassword.title}
      navigateBack={pageConfigs.validateOtpResetPassword.navigateBack()}
      buttonText={pageConfigs.validateOtpResetPassword.buttonLabel}
      thereIsButton
      loading={isLoading}
      onClick={handleValidateOtpCode}
      disabled={isLoading || (code.length !== codeLength)}
    >
      <FactorValidationWrapper>
        <TwoFactorValidationCodeComponent
          code={code}
          setIsResendDisabled={setIsResendDisabled}
          isResendDisabled={isResendDisabled}
          channelValue={channelValue}
          onCodeChange={handleInputChange}
          onSubmit={handleValidateOtpCode}
          onResend={handleResend}
        />
      </FactorValidationWrapper>
    </PageContainer>
  );
};
